import { useEffect, useState } from "react";
import { Phone, CalendarCheck, Star, MapPin } from "lucide-react";
import { SITE, HERO_SLIDES } from "../lib/site";
import { Link } from "react-router-dom";

const SLIDE_INTERVAL = 4000; // ms

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      setCurrent((c) => (c + 1) % HERO_SLIDES.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <section
      id="home"
      data-testid="hero-section"
      translate="no"
      lang="it"
      className="notranslate relative pt-[120px] sm:pt-[140px] pb-20 sm:pb-28 overflow-hidden"
    >
      {/* soft background blobs */}
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full opacity-60 blur-3xl"
        style={{ background: "radial-gradient(circle, var(--ee-blush) 0%, transparent 65%)" }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-32 w-[460px] h-[460px] rounded-full opacity-50 blur-3xl"
        style={{ background: "radial-gradient(circle, #F8DCD0 0%, transparent 65%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <div className="ee-eyebrow ee-reveal" data-testid="hero-eyebrow">
            Centro estetico a {SITE.city}
          </div>

          <h1
            data-testid="hero-title"
            className="font-serif-display ee-reveal ee-reveal-delay-1 mt-5 text-[42px] leading-[1.05] sm:text-[58px] sm:leading-[1.04] lg:text-[68px] lg:leading-[1.02] text-[var(--ee-ink)] tracking-[-0.015em]"
          >
            Bellezza, benessere e solarium
            <br className="hidden sm:block" />
            <em className="not-italic text-[var(--ee-rose-strong)] font-serif-display italic">
              {" "}in un centro curato
            </em>{" "}
            e professionale.
          </h1>

          <p
            data-testid="hero-subtitle"
            className="ee-reveal ee-reveal-delay-2 mt-7 max-w-xl text-[15px] sm:text-[16px] leading-[1.7] text-[var(--ee-ink-soft)]"
          >
            Estetica Elite accompagna da oltre 10 anni chi cerca trattamenti viso, corpo,
            mani, piedi, epilazione e solarium in un ambiente accogliente e
            professionale a Mestre.
          </p>

          <div
            data-testid="hero-ctas"
            className="ee-reveal ee-reveal-delay-3 mt-9 flex flex-wrap gap-3"
          >
            <Link
              to="/prenota"
              data-testid="hero-book-btn"
              className="ee-btn ee-btn-primary"
            >
              <CalendarCheck size={16} strokeWidth={1.8} />
              Prenota online
            </Link>
            <a
              href={SITE.phoneTel}
              data-testid="hero-call-btn"
              className="ee-btn ee-btn-outline"
            >
              <Phone size={16} strokeWidth={1.8} />
              Chiama ora
            </a>
          </div>

          {/* Trust row */}
          <div
            data-testid="trust-row"
            className="ee-reveal ee-reveal-delay-4 mt-12 flex flex-wrap gap-x-8 gap-y-4 items-center"
          >
            <TrustItem label="Dal 2015" />
            <TrustItem
              label={
                <span className="flex items-center gap-1.5">
                  <Star
                    size={14}
                    className="fill-[var(--ee-rose-strong)] text-[var(--ee-rose-strong)]"
                  />
                  4,3/5 su Google
                </span>
              }
            />
            <TrustItem label="130 recensioni" />
            <TrustItem
              label={
                <span className="flex items-center gap-1.5">
                  <MapPin size={13} strokeWidth={1.8} />
                  Venezia Mestre
                </span>
              }
            />
          </div>
        </div>

        {/* Hero slideshow */}
        <div className="lg:col-span-5 relative">
          <div
            className="relative ee-reveal ee-reveal-delay-2"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            data-testid="hero-slideshow"
          >
            <div className="ee-img-frame aspect-[4/5] shadow-[0_30px_70px_-40px_rgba(184,90,122,0.35)] relative">
              {HERO_SLIDES.map((slide, i) => (
                <img
                  key={slide.src}
                  src={slide.src}
                  alt={slide.alt}
                  loading={i === 0 ? "eager" : "lazy"}
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out"
                  style={{
                    opacity: i === current ? 1 : 0,
                    objectPosition: slide.objectPosition || "center",
                  }}
                  data-testid={`hero-slide-${i}`}
                />
              ))}
            </div>

            {/* Dot indicators */}
            <div
              className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10"
              data-testid="hero-slide-dots"
            >
              {HERO_SLIDES.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Vai alla slide ${i + 1}`}
                  onClick={() => setCurrent(i)}
                  className="w-2 h-2 rounded-full transition-all duration-300"
                  style={{
                    background:
                      i === current
                        ? "var(--ee-ivory)"
                        : "rgba(250, 246, 241, 0.5)",
                    width: i === current ? "22px" : "8px",
                  }}
                />
              ))}
            </div>

            {/* Google rating badge */}
            <div className="absolute -top-5 -right-3 sm:-right-6 bg-white/90 backdrop-blur-sm border border-[var(--ee-line)] rounded-2xl px-4 py-3 shadow-[0_10px_30px_-15px_rgba(42,37,32,0.25)] z-10">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ee-ink-soft)] font-medium">
                Google
              </div>
              <div className="flex items-baseline gap-1.5 mt-0.5">
                <span className="font-serif-display text-2xl text-[var(--ee-ink)]">
                  4,3
                </span>
                <span className="text-[11px] text-[var(--ee-ink-soft)]">
                  / 130 recensioni
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const TrustItem = ({ label }) => (
  <div className="flex items-center gap-3 text-[13px] text-[var(--ee-ink-soft)] font-medium">
    <span className="w-1.5 h-1.5 rounded-full bg-[var(--ee-rose-strong)]" />
    {label}
  </div>
);
